// src/components/admin/AdminOrderRequests.jsx

import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { HiRefresh, HiInformationCircle, HiXCircle } from "react-icons/hi";
import { Package, FileText, ChevronRight } from "lucide-react";
import {
  fetchPendingOrders,
  updateDeliveryStatus,
} from "../../features/orderSlice";
import { motion } from "framer-motion";
import ConfirmModal from "./ConfirmModal";
import { toast } from "react-hot-toast";
import { Link } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import axiosInstance from "../../utils/axiosInstance";

const NEXT_STATUS = {
  PROCESSING: ["SHIPPED"],
  SHIPPED: ["ON_THE_WAY"],
  ON_THE_WAY: ["BOOK_WITH_ME"],
  BOOK_WITH_ME: [],
};

// Status badge configuration - Enhanced contrast for light mode
const STATUS_CONFIG = {
  PROCESSING: {
    label: "Processing",
    color: "bg-gray-100/80 text-gray-700 border-gray-200/80 dark:bg-gray-800/80 dark:text-gray-200 dark:border-gray-700/80",
    dotColor: "bg-gray-500 dark:bg-gray-400",
  },
  SHIPPED: {
    label: "Shipped",
    color: "bg-blue-50/80 text-blue-700 border-blue-200/80 dark:bg-blue-900/40 dark:text-blue-200 dark:border-blue-800/80",
    dotColor: "bg-blue-500 dark:bg-blue-400",
  },
  ON_THE_WAY: {
    label: "On the Way",
    color: "bg-indigo-50/80 text-indigo-700 border-indigo-200/80 dark:bg-indigo-900/40 dark:text-indigo-200 dark:border-indigo-800/80",
    dotColor: "bg-indigo-500 dark:bg-indigo-400",
  },
  BOOK_WITH_ME: {
    label: "Delivered",
    color: "bg-green-50/80 text-green-700 border-green-200/80 dark:bg-green-900/40 dark:text-green-200 dark:border-green-800/80",
    dotColor: "bg-green-500 dark:bg-green-400",
  },
  CANCELLED: {
    label: "Cancelled",
    color: "bg-red-50/80 text-red-700 border-red-200/80 dark:bg-red-900/40 dark:text-red-200 dark:border-red-800/80",
    dotColor: "bg-red-500 dark:bg-red-400",
  },
};

// Workflow steps for visualization
const WORKFLOW_STEPS = ["PROCESSING", "SHIPPED", "ON_THE_WAY", "BOOK_WITH_ME"];

export default function AdminOrderRequests() {
  const dispatch = useDispatch();
  const { pendingOrders = [], status } = useSelector((s) => s.orders);
  const [confirmData, setConfirmData] = useState(null); // { orderId, nextStatus, actionType }
  const [downloading, setDownloading] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  // Filter out BOOK_WITH_ME (already delivered) and CANCELLED orders
  let filteredOrders = pendingOrders.filter(
    (order) => order.deliveryStatus !== "BOOK_WITH_ME" && order.deliveryStatus !== "CANCELLED"
  );

  // Apply date range filter if dates are selected
  if (startDate || endDate) {
    filteredOrders = filteredOrders.filter((order) => {
      const orderDate = new Date(
        order.creationDate[0],
        order.creationDate[1] - 1,
        order.creationDate[2]
      );
      orderDate.setHours(0, 0, 0, 0);

      if (startDate && endDate) {
        const start = new Date(startDate);
        start.setHours(0, 0, 0, 0);
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        return orderDate >= start && orderDate <= end;
      } else if (startDate) {
        const start = new Date(startDate);
        start.setHours(0, 0, 0, 0);
        return orderDate >= start;
      } else if (endDate) {
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        return orderDate <= end;
      }
      return true;
    });
  }

  useEffect(() => {
    dispatch(fetchPendingOrders());
  }, [dispatch]);
  const handleDeliveryAdvance = (orderId, currentStatus) => {
    const [next] = NEXT_STATUS[currentStatus] || [];
    if (!next) return;

    setConfirmData({
      orderId,
      nextStatus: next,
      actionType: "advance",
    });
  };

  const handleCancelDelivery = (orderId, currentStatus) => {
    if (["RETURNED", "CANCELLED"].includes(currentStatus)) return;

    setConfirmData({
      orderId,
      nextStatus: "CANCELLED",
      actionType: "cancel",
    });
  };

  
  function formatLocalDateAsYYYYMMDD(d) {
  if (!d) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0"); // months are 0-indexed
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}


 const handleGeneratePdf = async () => {
  if (!startDate) return toast.error("Please select a start date");

  setDownloading(true);
  try {
    // Use local-safe formatting (avoid toISOString())
    const formattedStartDate = formatLocalDateAsYYYYMMDD(startDate);
    
    let url = `/pdf/orders?date=${formattedStartDate}`;
    let filename = `orders_${formattedStartDate}`;
    
    // Add endDate if selected
    if (endDate) {
      const formattedEndDate = formatLocalDateAsYYYYMMDD(endDate);
      url += `&endDate=${formattedEndDate}`;
      filename = `orders_${formattedStartDate}_to_${formattedEndDate}`;
    }
    filename += '.pdf';

    console.log('PDF Request URL:', url);
    console.log('Start Date:', startDate);
    console.log('End Date:', endDate);
    console.log('Formatted Start:', formattedStartDate);
    if (endDate) {
      console.log('Formatted End:', formatLocalDateAsYYYYMMDD(endDate));
    }

    const response = await axiosInstance.get(url, {
      responseType: "blob",
    });

    const blob = new Blob([response.data], { type: "application/pdf" });
    const urlObj = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = urlObj;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(urlObj);

    toast.success("PDF generated successfully!");
  } catch (err) {
    console.error(err);
    toast.error("Failed to generate PDF");
  } finally {
    setDownloading(false);
  }
};



  const confirmAction = () => {
    if (!confirmData) return;

    const { orderId, nextStatus } = confirmData;

    dispatch(
      updateDeliveryStatus({ orderId, deliveryStatus: nextStatus })
    ).then(() => {
      toast.success(
        `Order #${orderId} marked as ${nextStatus.replace(/_/g, " ")}`
      );
      dispatch(fetchPendingOrders());
    });

    setConfirmData(null);
  };

  return (
    <div className="p-6 bg-gray-50 dark:bg-gray-950 min-h-screen">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header - Clean & Modern */}
        <header className="space-y-1">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                Book Order Requests
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                Manage delivery workflow and requests
              </p>
            </div>
            
            <button
              onClick={() => dispatch(fetchPendingOrders())}
              className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-800 transition-colors duration-150"
              aria-label="Refresh orders"
            >
              <HiRefresh className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </button>
          </div>
        </header>

        {/* Subtle Guidance Banner */}
        <div className="flex items-start gap-3 p-3.5 bg-gray-100/60 dark:bg-gray-900/60 border border-gray-200/60 dark:border-gray-800/80 rounded-lg">
          <HiInformationCircle className="w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed">
            Advance delivery one step at a time (Processing → Shipped → On the way → Delivered), or cancel delivery using the action buttons.
          </p>
        </div>

        {/* Date Range Filter Section */}
        <div className="bg-white dark:bg-gray-900/60 border border-gray-200/60 dark:border-gray-800/80 rounded-xl shadow-sm dark:shadow-black/10 p-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-gray-400 dark:text-gray-500" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Filter Orders:</span>
            </div>
            
            <div className="flex items-center gap-3 flex-1">
              <div className="flex-1 max-w-xs">
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">From Date</label>
                <DatePicker
                  selected={startDate}
                  onChange={(date) => setStartDate(date)}
                  selectsStart
                  startDate={startDate}
                  endDate={endDate}
                  maxDate={endDate || new Date()}
                  dateFormat="yyyy-MM-dd"
                  placeholderText="Select start date"
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-700/80 rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 bg-white dark:bg-gray-800/60 dark:text-gray-100 transition-all duration-150"
                  isClearable
                />
              </div>
              
              <div className="flex-1 max-w-xs">
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">To Date</label>
                <DatePicker
                  selected={endDate}
                  onChange={(date) => setEndDate(date)}
                  selectsEnd
                  startDate={startDate}
                  endDate={endDate}
                  minDate={startDate}
                  maxDate={new Date()}
                  dateFormat="yyyy-MM-dd"
                  placeholderText="Select end date"
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-700/80 rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 bg-white dark:bg-gray-800/60 dark:text-gray-100 transition-all duration-150"
                  isClearable
                />
              </div>
              
              {(startDate || endDate) && (
                <button
                  onClick={() => {
                    setStartDate(null);
                    setEndDate(null);
                  }}
                  className="mt-5 px-3 py-2 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800/60 rounded-lg transition-all duration-150"
                >
                  Clear Filter
                </button>
              )}
              
              <button
                onClick={handleGeneratePdf}
                disabled={!startDate || downloading}
                className="mt-5 px-4 py-2.5 text-sm font-semibold rounded-lg bg-green-600 hover:bg-green-700 text-white disabled:opacity-50 disabled:bg-gray-400 disabled:cursor-not-allowed transition-all duration-150 shadow-md hover:shadow-lg flex items-center gap-2"
              >
                <FileText className="w-4 h-4" />
                {downloading ? "Generating PDF..." : "Generate PDF"}
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        {status === "loading" ? (
          <div className="text-center py-20 text-gray-500 dark:text-gray-400">
            Loading orders…
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800">
            <Package className="w-12 h-12 text-gray-300 dark:text-gray-700 mb-3" />
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">No pending orders</p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">All orders have been processed</p>
          </div>
        ) : (
          <div className="overflow-hidden bg-white dark:bg-gray-900/60 rounded-xl shadow-xl dark:shadow-black/20 border border-gray-200/60 dark:border-gray-800/80">
            <div className="overflow-x-auto custom-scrollbar">
              <style>{`
                .custom-scrollbar::-webkit-scrollbar {
                  height: 6px;
                }
                .custom-scrollbar::-webkit-scrollbar-track {
                  background: transparent;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                  background-color: rgb(203 213 225 / 0.6);
                  border-radius: 3px;
                  transition: background-color 0.15s;
                }
                .dark .custom-scrollbar::-webkit-scrollbar-thumb {
                  background-color: rgb(71 85 105 / 0.8);
                }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                  background-color: rgb(148 163 184);
                }
                .dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                  background-color: rgb(100 116 139);
                }
              `}</style>
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800/80">
                <thead className="bg-gray-50/60 dark:bg-gray-900/80 border-b border-gray-200/60 dark:border-gray-800/80">
                  <tr>
                    <th className="px-3 py-3 text-left text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Order ID
                    </th>
                    <th className="px-3 py-3 text-left text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Book
                    </th>
                    <th className="px-3 py-3 text-left text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-3 py-3 text-left text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-3 py-3 text-left text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Workflow Status
                    </th>
                    <th className="px-3 py-3 text-left text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>

                <motion.tbody
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5 }}
                  className="divide-y divide-gray-100 dark:divide-gray-800/60"
                >
                  {filteredOrders.map((o) => {
                    const date = new Date(
                      o.creationDate[0],
                      o.creationDate[1] - 1,
                      o.creationDate[2],
                      o.creationDate[3] || 0,
                      o.creationDate[4] || 0
                    );
                    
                    const dateStr = date.toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    });
                    
                    const timeStr = date.toLocaleTimeString("en-GB", {
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: true,
                    }).toUpperCase();

                    const nextStatus = NEXT_STATUS[o.deliveryStatus]?.[0];
                    const isTerminal = o.deliveryStatus === "BOOK_WITH_ME";
                    const currentStepIndex = WORKFLOW_STEPS.indexOf(o.deliveryStatus);

                    return (
                      <tr
                        key={o.orderId}
                        className="hover:bg-gray-50/80 dark:hover:bg-white/[0.05] transition-all duration-150 hover:shadow-sm dark:hover:shadow-black/10"
                      >
                        {/* Order ID */}
                        <td className="px-3 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">
                          #{o.orderId}
                        </td>

                        {/* Book - Title only, bold */}
                        <td className="px-3 py-3">
                          <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 max-w-xs truncate">
                            {o.book.bookName}
                          </div>
                        </td>

                        {/* User - Name + Email grouped */}
                        <td className="px-3 py-3">
                          <div className="space-y-0.5">
                            <Link
                              to={`/admin/users/${o.user.userId}`}
                              className="text-sm font-medium text-gray-900 dark:text-gray-100 hover:text-orange-600 dark:hover:text-orange-400 transition-colors duration-150"
                            >
                              {o.user.firstName} {o.user.lastName}
                            </Link>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {o.user.emailId ?? "–"}
                            </div>
                          </div>
                        </td>

                        {/* Date - Two lines with muted time */}
                        <td className="px-3 py-3">
                          <div className="space-y-0.5">
                            <div className="text-xs font-medium text-gray-700 dark:text-gray-200 whitespace-nowrap">
                              {dateStr}
                            </div>
                            <div className="text-[10px] text-gray-500 dark:text-gray-400 whitespace-nowrap">
                              {timeStr}
                            </div>
                          </div>
                        </td>

                        {/* Workflow Status - Enhanced step indicator with connecting lines */}
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-3">
                            {/* Current Status Badge - Enhanced */}
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border transition-all duration-150 hover:shadow-sm ${STATUS_CONFIG[o.deliveryStatus]?.color || STATUS_CONFIG.PROCESSING.color}`}>
                              {STATUS_CONFIG[o.deliveryStatus]?.label || o.deliveryStatus.replace(/_/g, " ")}
                            </span>
                            
                            {/* Mini Workflow Indicator - Refined with better spacing */}
                            <div className="flex items-center">
                              {WORKFLOW_STEPS.map((step, idx) => {
                                const isActive = idx <= currentStepIndex;
                                const isCurrent = idx === currentStepIndex;
                                return (
                                  <div key={step} className="flex items-center">
                                    <div
                                      className={`rounded-full transition-all duration-200 ${
                                        isCurrent 
                                          ? "w-1.5 h-1.5" 
                                          : "w-1 h-1"
                                      } ${
                                        isActive
                                          ? STATUS_CONFIG[o.deliveryStatus]?.dotColor || "bg-gray-400"
                                          : "bg-gray-300 dark:bg-gray-700 opacity-40"
                                      }`}
                                    />
                                    {idx < WORKFLOW_STEPS.length - 1 && (
                                      <div
                                        className={`w-2.5 h-[1.5px] transition-all duration-200 ${
                                          idx < currentStepIndex
                                            ? STATUS_CONFIG[o.deliveryStatus]?.dotColor || "bg-gray-400"
                                            : "bg-gray-300 dark:bg-gray-700 opacity-30"
                                        }`}
                                      />
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </td>

                        {/* Actions - Refined ghost buttons with softer red */}
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-2">
                            {/* Advance Button - Ghost style */}
                            {nextStatus ? (
                              <button
                                onClick={() =>
                                  handleDeliveryAdvance(o.orderId, o.deliveryStatus)
                                }
                                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800/60 hover:text-gray-900 dark:hover:text-gray-100 transition-all duration-150"
                              >
                                {STATUS_CONFIG[nextStatus]?.label || nextStatus.replace(/_/g, " ")}
                                <ChevronRight className="w-3 h-3" />
                              </button>
                            ) : (
                              <span className="text-xs text-gray-400 dark:text-gray-600">
                                —
                              </span>
                            )}

                            {/* Cancel Button - Ghost style with softer red */}
                            <button
                              onClick={() =>
                                handleCancelDelivery(o.orderId, o.deliveryStatus)
                              }
                              disabled={isTerminal}
                              className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg transition-all duration-150 ${
                                isTerminal
                                  ? "bg-gray-100 dark:bg-gray-800/60 text-gray-400 dark:text-gray-600 cursor-not-allowed"
                                  : "text-red-600/90 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-700 dark:hover:text-red-200"
                              }`}
                            >
                              <HiXCircle className="w-3.5 h-3.5" />
                              Cancel
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </motion.tbody>
              </table>
            </div>
          </div>
        )}
      </div>
      
      {confirmData && (
        <ConfirmModal
          isOpen={true}
          title={
            confirmData.actionType === "cancel"
              ? "Cancel Order?"
              : "Advance Delivery?"
          }
          message={`Are you sure you want to mark order #${
            confirmData.orderId
          } as "${confirmData.nextStatus.replace(/_/g, " ")}"?`}
          onConfirm={confirmAction}
          onCancel={() => setConfirmData(null)}
        />
      )}
    </div>
  );
}
